import { useEffect, useRef, useState } from 'react';

import { Button } from "@/components/ui/button";
import {
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
} from "@/components/ui/tabs";

import { IconWrapper } from '@/components/atoms/icon-wrapper';
import { confirmDialog } from '@/components/confirm-dialog';
import ErrorPopup from '@/components/custom/InfoPopups/ErrorPopup';
import SuccessPopup from '@/components/custom/InfoPopups/SuccessPopup';
import { Form } from '@/components/ui/form';
import { useCreateCrmWithProjects } from '@/features/crm/hooks/useMutation/useCreateCrmWithProjects';
import { CreateCrmWithProjectsResult } from '@/features/crm/hooks/useMutation/useCreateCrmWithProjects/useCreateCrmWithProjects.query';
import { crmRegistry } from '@/features/crm_system/lib/_core/crmRegistry';
import { AddCrmModalStep } from '@/features/crm_system/lib/_core/crmSystem';
import { dialog, DialogRendererProps } from '@/lib/dynamic-dialog-state';
import { useOrganizationStore } from '@/lib/zustand/useStore';
import { zodResolver } from '@hookform/resolvers/zod';
import { FileSymlinkIcon } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useStep } from "usehooks-ts";
import { addCrmModalSimplifiedSchema, AddCrmModalSimplifiedSchemaType } from '.';
import { connectionsWizardDialogForProjectsWithoutConnections } from '../../../../connection-wizard/components/organisms/connection-wizard-dialog/connections-wizard-dialog';
import { AddCrmModalSimplifiedContextProvider } from './context/AddCrmModalSimplifiedContext';
import { predefinedStepsSimplified } from './lib/predefined-steps';

function AddCrmModalRaw({ predefinedSteps, ...props }: { predefinedSteps: AddCrmModalStep[] } & DialogRendererProps<CreateCrmWithProjectsResult>) {
    const { organization } = useOrganizationStore();

    const [isCurrentStepValid, setIsCurrentStepValid] = useState(false);

    const form = useForm<AddCrmModalSimplifiedSchemaType>({
        resolver: zodResolver(addCrmModalSimplifiedSchema),
        defaultValues: {
            name: "",
            project_ids: [],
        }
    })

    const pickedCrmSystem = form.watch("crm_system");
    const crmSystemSteps = crmRegistry.addCrmModalLimitedStepsResolver(pickedCrmSystem || null);
    const predefinedStepsCount = predefinedSteps.length;
    const [step, { goToNextStep, setStep, canGoToPrevStep, goToPrevStep, canGoToNextStep: _canGoToNextStep, reset }] = useStep(crmSystemSteps.length + predefinedStepsCount);
    const onNextHandlerRef = useRef<(() => boolean) | undefined>(undefined);
    const onBackHandlerRef = useRef<(() => boolean) | undefined>(undefined);
    const [nextButtonLabel, setNextButtonLabel] = useState<React.ReactNode>();
    const { mutateAsync: createCrmWithProjectsAsync, isPending: isCreatingCrm } = useCreateCrmWithProjects()
    const [createProjectsAfterCrmCreation, setCreateProjectsAfterCrmCreation] = useState(false);
    const onSubmitHandler = async (data: AddCrmModalSimplifiedSchemaType) => {

        const { project_ids, crm_project, ...crmData } = data;
        try {
            const res = await createCrmWithProjectsAsync({
                crm: {
                    ...crmData,
                    organisation_id: Number(organization?.id),
                },
                updateProjectsIds: project_ids,
                crm_project: crm_project,
                createProjectsAutomatically: createProjectsAfterCrmCreation
            })
            if(createProjectsAfterCrmCreation){
                SuccessPopup("CRM configuration and projects created successfully");
            }else{
                SuccessPopup("CRM configuration created successfully");
            }
            props.confirm(res)
            props.closeDialog();
        } catch (error) {
            console.error(error);
            ErrorPopup(`Error creating CRM configuration`);
        }
    }
    const [canGoToNextStep, setCanGoToNextStep] = useState(_canGoToNextStep);

    const isLastStep = !canGoToNextStep;

    const isCurrentStepInvalid = !isCurrentStepValid;

    const isLastStepAndFormValid = isLastStep && form.formState.isValid;
    const shouldDisableOnLastStep = isLastStepAndFormValid && isCurrentStepInvalid;

    const shouldDisableOnIntermediateStep = !isLastStep && isCurrentStepInvalid;

    const isSubmitDisabled = (shouldDisableOnLastStep && !isCreatingCrm) || shouldDisableOnIntermediateStep;

    const handleNext = () => {
        const handled = onNextHandlerRef.current?.() ?? false;
        if (!handled) {
            goToNextStep();
        }
    };

    const handleBack = () => {
        const handled = onBackHandlerRef.current?.() ?? false;
        if (!handled) {
            goToPrevStep();
        }
    };

    const submitButtonHandler = canGoToNextStep ? handleNext : form.handleSubmit(onSubmitHandler);

    const getNextButtonLabel = () => {
        if (nextButtonLabel) {
            return nextButtonLabel;
        }
        if (canGoToNextStep) {
            return "Next";
        }
        if (isCreatingCrm) {
            return "Creating...";
        }
        return "Finish";
    }
    useEffect(() => {
        setCanGoToNextStep(_canGoToNextStep);
    }, [_canGoToNextStep, step])
    return (
        <DialogContent className='max-w-7xl'>
            <AddCrmModalSimplifiedContextProvider
                value={{
                    isCurrentStepValid,
                    setIsCurrentStepValid,
                    step,
                    setStep,
                    goToNextStep,
                    goToPrevStep,
                    canGoToNextStep,
                    canGoToPrevStep,
                    reset,
                    setCanGoToNextStep,
                    setOnNextHandler: (handler?: () => boolean) => { onNextHandlerRef.current = handler; },
                    setOnBackHandler: (handler?: () => boolean) => { onBackHandlerRef.current = handler; },
                    setNextButtonLabel,
                    createProjectsAfterCrmCreation,
                    setCreateProjectsAfterCrmCreation,
                }}
            >
                <div className="">
                    <DialogHeader>
                        <DialogTitle className='m-0 font-bold text-start'>
                            Create CRM configuration
                        </DialogTitle>
                        <DialogDescription>
                            Structured setup workflow that facilitates the creation and configuration of projects
                        </DialogDescription>
                    </DialogHeader>
                    <Form {...form}>
                        <form onSubmit={form.handleSubmit(onSubmitHandler)}>
                            <ScrollArea className="max-h-[60vh] sm:max-h-[70vh] pe-3 overflow-auto">
                                <div className="flex flex-col gap-4 px-1 mb-3">
                                    <Tabs defaultValue="1" value={String(step)}>
                                        <TabsList className={`flex h-auto`}>
                                            {predefinedSteps.map(({ stepTitle: Title }, index) => (
                                                <TabsTrigger key={JSON.stringify(Title)} className="flex flex-col grow" value={String(index + 1)}>
                                                    <h2 className="">{`Step ${index + 1}`}</h2>
                                                    <p className="text-xs font-normal">{<Title />}</p>
                                                </TabsTrigger>
                                            ))}
                                            {crmSystemSteps.map(({ stepTitle: Title }, index) => (
                                                <TabsTrigger key={JSON.stringify(Title)} className="flex flex-col grow" value={String(index + 1 + predefinedStepsCount)}>
                                                    <h2 className="">{`Step ${index + 2}`}</h2>
                                                    <p className="text-xs font-normal">{<Title />}</p>
                                                </TabsTrigger>
                                            ))}
                                        </TabsList>
                                        {predefinedSteps.map(({ stepComponent: StepComponent, stepTitle: Title }, index) => (
                                            <TabsContent key={JSON.stringify(Title)} value={String(index + 1)}>
                                                <StepComponent
                                                    form={form}
                                                />
                                            </TabsContent>
                                        ))}
                                        {crmSystemSteps.map(({ stepComponent: StepComponent, stepTitle: Title }, index) => (
                                            <TabsContent key={JSON.stringify(Title)} value={String(index + 1 + predefinedStepsCount)}>
                                                <StepComponent
                                                    form={form}
                                                />
                                            </TabsContent>
                                        ))}
                                    </Tabs>
                                </div>
                            </ScrollArea>
                            <div className="flex items-stretch justify-between w-full pt-3 m-0 mt-3 border-t sm:space-x-2 max-sm:flex-col max-sm:space-y-2">
                                <Button type="button" variant="secondary" size='sm' onClick={props.cancel}>Cancel</Button>

                                <div className="flex m-0 sm:space-x-2 max-sm:flex-col max-sm:space-y-2">
                                    <Button
                                        type="button"
                                        variant="secondary"
                                        disabled={!canGoToPrevStep}
                                        onClick={handleBack}
                                        size='sm'
                                    >
                                        Back
                                    </Button>
                                    <Button
                                        type="button"
                                        disabled={isSubmitDisabled}
                                        onClick={submitButtonHandler}
                                        size='sm'
                                    >
                                        {getNextButtonLabel()}
                                    </Button>
                                </div>
                            </div>
                        </form>
                    </Form>
                </div>
            </AddCrmModalSimplifiedContextProvider>
        </DialogContent>
    )
}


function AddCrmModalSimplified(props: Readonly<DialogRendererProps<CreateCrmWithProjectsResult>>) {
    return <AddCrmModalRaw predefinedSteps={predefinedStepsSimplified} {...props} />
}

export const addCrmModalSimplified = dialog(AddCrmModalSimplified)

export const addCrmModalSimplifiedWithConnectionWizard = async () => {
    const createConnectionsResult = await addCrmModalSimplified().async()
    if (!createConnectionsResult.isConfirmed) return;
    // if(createConnectionsResult.value?.project?.length) return;
    const doWishToOpenConnectionsWizard = await confirmDialog({
        title: "Do you want to set up CRM connections now?",
        description: "You can set up CRM connections later, but setting them up now will allow you to start exporting data to your CRM right away.",
        icon: (
            <IconWrapper size='default' variant='default' background={'opaque'}>
                <FileSymlinkIcon />
            </IconWrapper>
        ),
        confirmTextButton: (props) => (
            <Button
                type="button"
                onClick={() => props.confirm()}
                variant={"default"}
                size='sm'
            >
                Yes
            </Button>
        ),
        cancelTextButton: "No, I'll do it later"
    }).async().then(({ isConfirmed }) => isConfirmed)

    if (doWishToOpenConnectionsWizard && createConnectionsResult.value) {
        connectionsWizardDialogForProjectsWithoutConnections({ connectionWithProjectsAndSurveysConnections: createConnectionsResult.value });
    }
}